import { c as _c } from "react/compiler-runtime";
import type { MentoringDiscount } from '@algoacademy/backend/modules/payment/discount-pricing';
import type { GroupMentoringProductDTO, IndividualMentoringProductDTO, ProjectMentoringProductDTO } from '@algoacademy/backend/modules/payment/endpoints/mutations/stripe-checkout/input.dto/input.dto';
import { ORDER_TYPE } from '@algoacademy/backend/modules/payment/endpoints/mutations/stripe-checkout/types';
import { cn } from '@algoacademy/design-system/shared/cn';
import { Badge } from '@algoacademy/design-system/web/data-display/Badge';
import { H3 } from '@algoacademy/design-system/web/marketing/typography/Heading/H3';
import { P3 } from '@algoacademy/design-system/web/marketing/typography/Paragraph';
import { readDiscountLadder } from './_components/DiscountControls/_helpers/discount-ladder';
import { readBuyDataDisplayModel } from './_helpers/buy-data-display';
import { calculateGroupMentoringPrice, calculateIndividualMentoringPrice, calculateMentoringSubscriptionPrice, calculateProjectMentoringPrice, MENTORING_SUBSCRIPTION_DISCOUNT_PERCENT, roundUpToNearestZloty } from './prices';
type BuyDataProps = {
  mentoringProduct: GroupMentoringProductDTO | IndividualMentoringProductDTO | ProjectMentoringProductDTO;
  goalPage?: boolean;
  /**
   * The discount staff is pricing on the card above. Null for every reader who
   * may not manage payments, and then the card is the public one, down to the
   * pixel.
   */
  discount?: MentoringDiscount | null;
};
function computeBasePrice(mentoringProduct: BuyDataProps['mentoringProduct']): number {
  if (mentoringProduct.order === ORDER_TYPE.INDIVIDUAL_MENTORING) {
    return calculateIndividualMentoringPrice({
      hoursWithMentor: mentoringProduct.hoursWithMentor
    });
  }
  if (mentoringProduct.order === ORDER_TYPE.GROUP_MENTORING) {
    return calculateGroupMentoringPrice();
  }
  return calculateProjectMentoringPrice();
}
export function BuyData(t0) {
  const $ = _c(22);
  const {
    mentoringProduct,
    goalPage: t1,
    discount: t2
  } = t0;
  const goalPage = t1 === undefined ? false : t1;
  const discount = t2 === undefined ? null : t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== discount || $[1] !== goalPage || $[2] !== mentoringProduct) {
    const pricePerFourWeeks = computeBasePrice(mentoringProduct);
    const subscription = mentoringProduct.subscription;
    const discountPercent = MENTORING_SUBSCRIPTION_DISCOUNT_PERCENT[subscription] ?? 0;
    const priceCalculation = calculateMentoringSubscriptionPrice(pricePerFourWeeks, subscription);
    const originalPerFourWeeks = pricePerFourWeeks;
    const discountedPerFourWeeks = discountPercent > 0 ? roundUpToNearestZloty(pricePerFourWeeks * (100 - discountPercent) / 100) : pricePerFourWeeks;
    const ladder = discount === null ? null : readDiscountLadder({
      mentoringProduct,
      discount
    });
    const display = readBuyDataDisplayModel({
      subscription,
      discount,
      ladder,
      originalPerFourWeeks,
      discountedPerFourWeeks,
      discountPercent,
      priceCalculation
    });
    t7 = "w-full space-y-3";
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <P3 className="text-center text-base text-contentPrimary">23% VAT w cenie</P3>;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    t3 = "flex flex-col items-center gap-2 text-center";
    t4 = display.struckPrices.map(_temp);
    const t9 = goalPage && "text-gradient";
    let t10;
    if ($[10] !== t9) {
      t10 = cn("font-bold text-5xl lg:font-extrabold", t9);
      $[10] = t9;
      $[11] = t10;
    } else {
      t10 = $[11];
    }
    let t11;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <P3>/ 4 tygodnie</P3>;
      $[12] = t11;
    } else {
      t11 = $[12];
    }
    t5 = <div className="flex flex-row items-end gap-3"><p className={t10}>{display.finalPriceZloty} zł</p>{t11}</div>;
    t6 = display.pricingTable.length > 0 ? <dl className="mt-2 w-full max-w-xs border-borderSupport border-t text-sm">{display.pricingTable.map(_temp2)}</dl> : null;
    $[0] = discount;
    $[1] = goalPage;
    $[2] = mentoringProduct;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
    $[6] = t6;
    $[7] = t7;
    $[8] = t8;
  } else {
    t3 = $[3];
    t4 = $[4];
    t5 = $[5];
    t6 = $[6];
    t7 = $[7];
    t8 = $[8];
  }
  let t9;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t5 || $[16] !== t6) {
    t9 = <div className={t3}>{t4}{t5}{t6}</div>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t10 = <div className={t7}>{t8}{t9}</div>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  return t10;
}
function _temp2(row) {
  return <div key={`${row.kind}-${row.label}`} className={cn("flex items-baseline justify-between gap-4 border-borderSupport border-b p-1.5", row.green && "bg-backgroundGreen")}><dt className={cn("text-left", row.green ? "text-contentPrimary" : "text-contentTertiary")}>{row.label}</dt><dd className={cn("text-right font-medium tabular-nums", row.green ? "text-contentEmerald" : "text-contentSecondary")}><span>{row.amountZloty}</span>{row.cadenceSuffix !== null ? <span className={cn("ml-1 font-normal text-contentTertiary text-xs")}>{row.cadenceSuffix}</span> : null}</dd></div>;
}
function _temp(struckPrice) {
  return <div key={`${struckPrice.zloty}-${struckPrice.cadenceBadgePercent ?? "none"}-${struckPrice.staffDiscountLabel ?? "none"}`} className="flex flex-row items-center gap-2"><H3 className="line-through! relative font-normal! text-contentTertiary">{struckPrice.zloty} zł</H3>{struckPrice.cadenceBadgePercent !== null ? <Badge className="bg-blue-100 font-semibold text-algoprimary text-xs tabular-nums">-{struckPrice.cadenceBadgePercent}%</Badge> : null}{struckPrice.staffDiscountLabel !== null ? <span className="font-medium text-algoprimary text-sm tabular-nums">{struckPrice.staffDiscountLabel}</span> : null}</div>;
}